import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import PageHeader from '../components/PageHeader';
import ProviderAvatar from '../components/ProviderAvatar';
import StatusBadge from '../components/StatusBadge';
import { Loading, ErrorState } from '../components/States';
import Icon from '../components/Icon';
import { ICONS } from '../lib/icons';

export default function Providers() {
  const { data, loading, error, reload } = useAsync(() => api.getProviders(), []);
  const { data: capabilities, reload: reloadCapabilities } = useAsync(
    () => api.getProviderCapabilities(),
    []
  );
  const [toggling, setToggling] = useState({});

  const handleToggleProvider = async (providerID) => {
    setToggling(prev => ({ ...prev, [providerID]: true }));
    try {
      const cap = capabilities?.find(c => c.providerId === providerID);
      await api.updateProviderCapability(providerID, { enabled: !cap?.enabled });
      await reloadCapabilities();
    } catch (err) {
      console.error('Failed to toggle provider:', err);
    } finally {
      setToggling(prev => ({ ...prev, [providerID]: false }));
    }
  };

  if (loading) return <Loading label="Loading providers…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="stack">
      <PageHeader
        title="Providers"
        subtitle="Connected cloud accounts and their status."
      />

      <div className="grid grid--cards">
        {data.map((p) => {
          const cap = capabilities?.find(c => c.providerId === p.id);
          return (
            <div key={p.id} className="provider-card">
              <Link to={`/providers/${p.id}`} className="provider-tile">
                <div className="provider-tile__head">
                  <ProviderAvatar provider={p} size={44} logo />
                  <StatusBadge status={p.status} />
                </div>
                <div className="provider-tile__name">{p.name}</div>
                <div className="provider-tile__desc">{p.description}</div>
                <div className="provider-tile__foot">
                  <span className="provider-tile__region">{p.defaultRegion}</span>
                  <Icon name={ICONS.arrowRight} className="provider-tile__arrow" />
                </div>
              </Link>

              {cap && (
                <div className="provider-card__footer">
                  <div className="provider-features">
                    {cap.hasBilling && <span className="feature-badge">Billing</span>}
                    {cap.hasContracts && <span className="feature-badge">Contracts</span>}
                    {cap.hasPrices && <span className="feature-badge">Pricing</span>}
                    {cap.hasNetwork && <span className="feature-badge">Network</span>}
                    {cap.hasCompute && <span className="feature-badge">Compute</span>}
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={cap.enabled}
                      onChange={() => handleToggleProvider(p.id)}
                      disabled={toggling[p.id]}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">{cap.enabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
